/**
 * Matrix Chat/Video Service — singleton orchestrator.
 * Implementation is split across ./matrix*.js modules.
 */

import { createMatrixClient } from './matrixClient';
import {
  MATRIX_CHAT_BACKFILL_LIMIT,
  MATRIX_LIST_SYNC_LIMIT,
  MATRIX_POST_SYNC_HYDRATION_LIMIT,
  MATRIX_SERVER_URL,
  MATRIX_INBOX_SNAPSHOT_MAX_ROOMS,
  MATRIX_INBOX_SNAPSHOT_PERSIST_MS,
  SYNC_POLL_TIMEOUT_MS,
} from './matrixConstants';
import {
  clearMatrixCredentials,
  getUserInfo,
  handleTokenExpiry as handleTokenExpiryImpl,
  isTokenExpiredError,
  persistMatrixCredentials,
  refreshTokenAndUpdateClient as refreshTokenAndUpdateClientImpl,
  updateAccessToken,
} from './matrixAuth';
import { registerClientListener as attachMatrixClientListener, setupAuthErrorHandlers } from './matrixEvents';
import {
  awaitReadySyncState,
  exclusiveStartMatrixClient,
  restartSyncImpl,
  scheduleRecoveryAttemptsReset,
  waitForInitialTimeline,
} from './matrixSync';
import {
  ensureRoomReady as ensureRoomReadyImpl,
  hydrateRoomTimelinesAfterSync,
  logTimelineDebug,
  paginateRoomIfNeeded as paginateRoomIfNeededImpl,
  schedulePostSyncTimelineHydration,
} from './matrixTimeline';
import { setupUnreadTotalTracking, teardownUnreadTotalTracking } from './matrixUnread';
import {
  clearMatrixTabHiddenOnColdStart,
  subscribeMatrixTabResume,
  clearMatrixSyncClock,
} from './matrixTabResume';
import { isAbortError, isHealthySyncState, isSameMatrixClient, safeListener } from './matrixUtils';
import { captureChatException, captureChatMessage } from 'shared/sentry';
import { getLocalItem, setLocalItem, removeLocalItem } from '../../utils/config/localStorage';
import { AUTH_TOKEN_KEYS } from '../../constants/common';
import { isMatrixAccessTokenStale } from './matrixTokenFreshness';

export {
  MATRIX_LIST_SYNC_LIMIT,
  MATRIX_POST_SYNC_HYDRATION_LIMIT,
} from './matrixConstants';

class Matrix {
  constructor() {
    this.matrixClient = null;
    this.isInitialized = false;
    this.serverUrl = MATRIX_SERVER_URL;
    this.refreshPromise = null;
    this.syncRestartPromise = null;
    this.lastSyncRestartAt = 0;
    this.lastHardFailureAt = 0;
    this.recoveryAttempts = 0;
    this.maxRecoveryAttempts = 5;
    this.lastTokenToastAt = 0;
    /** @type {Promise<boolean>|null} */
    this._tokenExpiryHandling = null;
    this.initPromise = null;
    this.initGeneration = 0;
    /** @type {Promise<void>} */
    this._clientLifecycleOp = Promise.resolve();
    this._recoveryStableTimer = null;
    /** @type {Array<() => void>} */
    this._clientListenerDisposers = [];

    this.unreadTotal = 0;
    this.roomUnreadCache = new Map();
    this._unreadDirtyRoomIds = new Set();
    this._unreadFullRefreshPending = false;
    this.unreadTotalListeners = new Set();
    this.unreadTotalTimeout = null;
    this.unreadTotalHandlers = null;

    this._timelineReady = false;
    this.timelineReadyListeners = new Set();

    /** UI cache of inbox rows while getRooms() is empty (fresh initial /sync after clearing `since`). */
    this._inboxListSnapshot = [];
    this._inboxPersistTimer = null;
    /** Resume payload captured before the client existed (cold boot). */
    this._pendingTabResume = null;
    /** Merged restart options while a restart is already running. */
    this._pendingRestart = null;

    /** @type {Set<(payload: Object) => void>} */
    this.stateChangeListeners = new Set();

    this._browserOnlineAttached = false;
    this._onBrowserOnline = null;

    /** @type {(() => void)|null} */
    this._tabResumeUnsubscribe = null;

    this._unhandledRejectionAttached = false;
    this._onUnhandledRejection = null;

    this.onStateChange = undefined;
    this.onAuthFailure = undefined;

    this._attachBrowserOnlineListener();
    this._attachUnhandledRejectionLogger();
    this._ensureTabResumeSubscription();
  }

  _ensureTabResumeSubscription() {
    if (this._tabResumeUnsubscribe) return;
    this._tabResumeUnsubscribe = subscribeMatrixTabResume((payload) => {
      void this._handleTabResume(payload);
    });
  }

  /**
   * Resume after tab return (same client — do not reinitialize):
   * - Long idle: limited initial `/sync` (CS API with no `since`), matching login.
   *   Access token is refreshed first only if it is near/past expiry.
   * - Idle ≥30s: forced stop/start (small CATCHUP is acceptable).
   * - Brief hide: light recovery.
   * - Active call: `restartSync` defers so VoIP is not torn down.
   * @param {{ resumeFreshInitialSync?: boolean, longIdle?: boolean, source?: string }} payload
   */
  async _handleTabResume(payload = {}) {
    const { resumeFreshInitialSync, longIdle, source } = payload;
    if (!this.matrixClient || this.initPromise) {
      this._pendingTabResume = payload;
      return;
    }

    if (resumeFreshInitialSync) {
      // Token and `since` are independent. Mint only when the token would 401.
      // Keep the same MatrixClient (VoIP + open 1:1 timelines). Then one stop/start
      // omits `since` (CS API initial /sync) so we do not CATCHUP for hours.
      // restartSync defers if a call is live.
      const syncHealthy = isHealthySyncState(this.matrixClient.getSyncState?.());
      if (isMatrixAccessTokenStale(Date.now(), null, { treatUnknownAgeAsStale: !syncHealthy })) {
        await this.refreshTokenAndUpdateClient({ restartClient: false });
      }
      void this.requestSyncRecovery({
        reason: source ? `tab-resume-fresh:${source}` : 'tab-resume-fresh',
        initialSyncLimit: MATRIX_LIST_SYNC_LIMIT,
        force: true,
        minIntervalMs: 0,
        freshInitialSync: true,
      });
      return;
    }

    void this.requestSyncRecovery({
      reason: longIdle ? 'tab-visible-long-idle' : 'tab-visible',
      initialSyncLimit: MATRIX_LIST_SYNC_LIMIT,
      ...(longIdle ? { force: true, minIntervalMs: 0 } : {}),
    });
  }

  _attachUnhandledRejectionLogger() {
    if (typeof window === 'undefined' || this._unhandledRejectionAttached) return;
    this._onUnhandledRejection = (e) => {
      const reason = e?.reason;
      if (isAbortError(reason)) {
        // Avoid browser-level noise for expected Matrix long-poll aborts.
        try {
          e?.preventDefault?.();
        } catch (_) {}
        return;
      }
      const msg =
        (typeof reason?.message === 'string' && reason.message) ||
        (typeof reason?.toString === 'function' && reason.toString()) ||
        '';
      if (
        typeof msg === 'string' &&
        (msg.includes('Matrix') || msg.includes('M_UNKNOWN') || msg.includes('m.room'))
      ) {
        captureChatException(
          reason instanceof Error ? reason : new Error(String(reason)),
          { tags: { matrix: 'unhandled-rejection' } },
        );
      }
    };
    window.addEventListener('unhandledrejection', this._onUnhandledRejection);
    this._unhandledRejectionAttached = true;
  }

  _emitStateChange(payload) {
    try {
      this.onStateChange?.(payload);
    } catch (e) {
      captureChatException(e, { tags: { matrix: 'onStateChange' } });
    }
    for (const fn of this.stateChangeListeners) {
      try {
        fn(payload);
      } catch (e) {
        captureChatException(e, { tags: { matrix: 'stateChange-subscriber' } });
      }
    }
  }

  /**
   * @param {(payload: Object) => void} listener
   * @returns {() => void}
   */
  subscribeStateChange(listener) {
    if (typeof listener !== 'function') return () => {};
    this.stateChangeListeners.add(listener);
    return () => {
      this.stateChangeListeners.delete(listener);
    };
  }

  _invokeAuthFailure(detail = {}) {
    try {
      this.onAuthFailure?.(detail);
    } catch (e) {
      captureChatException(e, { tags: { matrix: 'onAuthFailure' } });
    }
    this._emitStateChange({ type: 'AUTH_FAILURE', ...detail });
  }

  _attachBrowserOnlineListener() {
    if (typeof window === 'undefined' || this._browserOnlineAttached) return;
    this._onBrowserOnline = () => {
      if (!this.matrixClient) return;
      void this.requestSyncRecovery({
        reason: 'network-online',
        force: true,
        minIntervalMs: 0,
      });
    };
    window.addEventListener('online', this._onBrowserOnline);
    this._browserOnlineAttached = true;
  }

  _detachBrowserOnlineListener() {
    if (typeof window === 'undefined' || !this._browserOnlineAttached || !this._onBrowserOnline) return;
    window.removeEventListener('online', this._onBrowserOnline);
    this._browserOnlineAttached = false;
  }

  _inboxSnapshotUserId() {
    return (
      this.matrixClient?.getUserId?.() ||
      getLocalItem(AUTH_TOKEN_KEYS.MATRIX_USER_ID, null) ||
      ''
    );
  }

  _readPersistedInboxListSnapshot() {
    try {
      const stored = getLocalItem(AUTH_TOKEN_KEYS.MATRIX_INBOX_SNAPSHOT, null);
      if (!stored || !Array.isArray(stored.rooms) || stored.rooms.length === 0) return [];
      const currentUser = this._inboxSnapshotUserId();
      if (!stored.userId || !currentUser || stored.userId !== currentUser) return [];
      return stored.rooms;
    } catch (_) {
      return [];
    }
  }

  _persistInboxListSnapshot() {
    if (this._inboxPersistTimer) {
      clearTimeout(this._inboxPersistTimer);
    }
    this._inboxPersistTimer = setTimeout(() => {
      this._inboxPersistTimer = null;
      try {
        const userId = this._inboxSnapshotUserId();
        const rooms = this._inboxListSnapshot.slice(0, MATRIX_INBOX_SNAPSHOT_MAX_ROOMS);
        if (!userId || !rooms.length) return;
        setLocalItem(AUTH_TOKEN_KEYS.MATRIX_INBOX_SNAPSHOT, {
          userId,
          savedAt: Date.now(),
          rooms,
        });
      } catch (_) {}
    }, MATRIX_INBOX_SNAPSHOT_PERSIST_MS);
  }

  getInboxListSnapshot() {
    if (Array.isArray(this._inboxListSnapshot) && this._inboxListSnapshot.length > 0) {
      return this._inboxListSnapshot.slice();
    }
    const persisted = this._readPersistedInboxListSnapshot();
    if (persisted.length > 0) {
      this._inboxListSnapshot = persisted;
      return persisted.slice();
    }
    return [];
  }

  setInboxListSnapshot(rooms) {
    if (!Array.isArray(rooms) || rooms.length === 0) return;
    this._inboxListSnapshot = rooms.slice();
    this._persistInboxListSnapshot();
  }

  /**
   * @param {{ persist?: boolean }} [options] persist=false keeps localStorage so a
   * re-login-like re-sync can still paint the last inbox immediately.
   */
  clearInboxListSnapshot({ persist = true } = {}) {
    this._inboxListSnapshot = [];
    if (this._inboxPersistTimer) {
      clearTimeout(this._inboxPersistTimer);
      this._inboxPersistTimer = null;
    }
    if (persist) {
      try {
        removeLocalItem(AUTH_TOKEN_KEYS.MATRIX_INBOX_SNAPSHOT);
      } catch (_) {}
    }
  }

  getTimelineReady() {
    return !!this._timelineReady;
  }

  subscribeTimelineReady(listener) {
    if (typeof listener !== 'function') return () => {};
    this.timelineReadyListeners.add(listener);
    try {
      listener(this.getTimelineReady());
    } catch (e) {
      captureChatException(e, { tags: { matrix: 'timelineReady-subscriber' } });
    }
    return () => {
      this.timelineReadyListeners.delete(listener);
    };
  }

  emitTimelineReady(ready) {
    const v = !!ready;
    if (this._timelineReady === v) return;
    this._timelineReady = v;
    for (const ln of this.timelineReadyListeners) {
      try {
        ln(v);
      } catch (e) {
        captureChatException(e, { tags: { matrix: 'timelineReady-emit' } });
      }
    }
  }

  isRefreshingToken() {
    return !!this.refreshPromise;
  }

  getUnreadTotal() {
    return Number.isFinite(this.unreadTotal) ? this.unreadTotal : 0;
  }

  subscribeUnreadTotal(listener) {
    if (typeof listener !== 'function') return () => {};
    this.unreadTotalListeners.add(listener);
    try {
      listener(this.getUnreadTotal());
    } catch (e) {
      captureChatException(e, { tags: { matrix: 'unread-subscriber' } });
    }
    return () => {
      this.unreadTotalListeners.delete(listener);
    };
  }

  emitUnreadTotal(total) {
    const safeTotal = Number.isFinite(total) ? total : 0;
    this.unreadTotal = safeTotal;
    for (const listener of this.unreadTotalListeners) {
      try {
        listener(safeTotal);
      } catch (e) {
        captureChatException(e, { tags: { matrix: 'unread-emit' } });
      }
    }
  }

  teardownUnreadTotalTracking() {
    teardownUnreadTotalTracking(this);
  }

  setupUnreadTotalTracking(myUserId = null) {
    setupUnreadTotalTracking(this, myUserId);
  }

  safeListener(fn, label = 'listener') {
    return safeListener(fn, label);
  }

  registerClientListener(client, event, handler) {
    attachMatrixClientListener(this, client, event, handler);
  }

  clearRegisteredClientListeners() {
    for (const off of this._clientListenerDisposers) {
      try {
        off();
      } catch (_) {}
    }
    this._clientListenerDisposers = [];
  }

  isAbortError(err) {
    return isAbortError(err);
  }

  isHealthySyncState(state) {
    return isHealthySyncState(state);
  }

  async requestSyncRecovery(options = {}) {
    try {
      return await restartSyncImpl(this, options);
    } catch (e) {
      if (!isAbortError(e)) {
        captureChatException(e, { tags: { matrix: 'sync', action: 'requestSyncRecovery' } });
      }
      return false;
    }
  }

  async restartSync(options = {}) {
    return this.requestSyncRecovery(options);
  }

  persistMatrixCredentials(data) {
    persistMatrixCredentials(data);
  }

  clearMatrixCredentials() {
    clearMatrixCredentials();
    this.clearInboxListSnapshot({ persist: true });
  }

  async getUserInfo(options = {}) {
    return getUserInfo(options);
  }

  updateAccessToken(newAccessToken) {
    updateAccessToken(this.matrixClient, newAccessToken);
  }

  async refreshTokenAndUpdateClient(options = {}) {
    return refreshTokenAndUpdateClientImpl(this, options);
  }

  async handleTokenExpiry(source = 'unknown', err = null) {
    return handleTokenExpiryImpl(this, source, err);
  }

  isTokenExpiredError(error) {
    return isTokenExpiredError(error);
  }

  setupAuthErrorHandlers() {
    setupAuthErrorHandlers(this);
  }

  assertSdkCapabilities() {
    const client = this.matrixClient;
    if (!client) return;
    if (typeof client.setRoomReadMarkers !== 'function') {
      captureChatMessage('Matrix SDK: client.setRoomReadMarkers is missing', 'warning', {
        tags: { matrix: 'sdk-capability' },
      });
    }
    if (typeof client.getSyncState !== 'function') {
      captureChatMessage('Matrix SDK: client.getSyncState is missing', 'warning', {
        tags: { matrix: 'sdk-capability' },
      });
    }
  }

  async awaitReadySyncState(options = {}) {
    return awaitReadySyncState(this, options);
  }

  async waitForInitialTimeline(options = {}) {
    return waitForInitialTimeline(this, options);
  }

  logTimelineDebug(context = 'sync') {
    logTimelineDebug(this, context);
  }

  async paginateRoomIfNeeded(room, limit = MATRIX_CHAT_BACKFILL_LIMIT) {
    return paginateRoomIfNeededImpl(this.matrixClient, room, limit, this);
  }

  async ensureRoomReady(room) {
    return ensureRoomReadyImpl(this.matrixClient, room, this);
  }

  async hydrateRoomTimelinesAfterSync(limit = MATRIX_POST_SYNC_HYDRATION_LIMIT, generation = null) {
    return hydrateRoomTimelinesAfterSync(this, limit, generation);
  }

  schedulePostSyncTimelineHydration(opts = {}) {
    schedulePostSyncTimelineHydration(this, opts);
  }

  async initialize(matrixUserInfo = null) {
    if (this.initPromise) return await this.initPromise;

    this.initPromise = (async () => {
      const gen = this.initGeneration;

      // Cold boot is the login path (empty MemoryStore + limited /sync). Drop hide marker.
      clearMatrixTabHiddenOnColdStart();

      const cleanupClient = (client) => {
        try {
          client?.stopClient?.();
        } catch (_) {}
        try {
          client?.removeAllListeners?.();
        } catch (_) {}
      };

      if (this.matrixClient) {
        this.clearRegisteredClientListeners();
        cleanupClient(this.matrixClient);
      }

      const user = matrixUserInfo ?? (await getUserInfo());
      if (gen !== this.initGeneration) return false;

      if (user?.success === false) {
        captureChatMessage(`Matrix initialization failed: ${user.reason}`, 'error', {
          extra: { reason: user.reason, error: user.error },
          tags: { matrix: 'init' },
        });
        this.isInitialized = false;
        return false;
      }

      if (!this.serverUrl) {
        this.isInitialized = false;
        return false;
      }

      if (!user?.matrix_access_token || !user?.matrix_user_id) {
        captureChatMessage('Matrix initialization failed: missing credentials', 'error', {
          tags: { matrix: 'init' },
        });
        this.isInitialized = false;
        return false;
      }

      const client = createMatrixClient({
        baseUrl: this.serverUrl,
        userId: user.matrix_user_id,
        accessToken: user.matrix_access_token,
        deviceId: user.matrix_device_id,
      });

      if (gen !== this.initGeneration) {
        cleanupClient(client);
        return false;
      }

      this.matrixClient = client;

      this.setupAuthErrorHandlers();
      this.assertSdkCapabilities();

      // VoIP handlers are ensured after startClient inside exclusiveStartMatrixClient
      // (stopClient clears callEventHandler; startClient does not recreate it).

      this.emitTimelineReady(false);

      await exclusiveStartMatrixClient(
        this,
        {
          initialSyncLimit: MATRIX_LIST_SYNC_LIMIT,
          pollTimeout: SYNC_POLL_TIMEOUT_MS,
          lazyLoadMembers: true,
        },
        { stopFirst: false },
      );

      await awaitReadySyncState(this, { generation: gen });

      if (gen !== this.initGeneration) {
        cleanupClient(client);
        if (isSameMatrixClient(this, client)) this.matrixClient = null;
        return false;
      }

      // matrix-js-sdk: PREPARED means /sync applied and getRooms() is valid.
      // Do not block the inbox on extra timeline events — those are for VoIP replay.
      this.setupUnreadTotalTracking(user.matrix_user_id || this.matrixClient.getUserId?.() || null);
      this.isInitialized = true;

      scheduleRecoveryAttemptsReset(this);
      this._attachBrowserOnlineListener();
      this._ensureTabResumeSubscription();
      // Mid-init resume is stored then dropped: this startClient already has no `since`.
      this._pendingTabResume = null;

      void (async () => {
        if (gen !== this.initGeneration) return;
        await waitForInitialTimeline(this, { generation: gen });
        if (gen !== this.initGeneration) return;
        this.emitTimelineReady(true);
        this.schedulePostSyncTimelineHydration({
          limit: MATRIX_POST_SYNC_HYDRATION_LIMIT,
          generation: gen,
          context: 'initialize',
        });
      })();

      return true;
    })();

    try {
      return await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  getDomain() {
    try {
      const url = new URL(this.serverUrl);
      return url.hostname;
    } catch (error) {
      captureChatException(error, { tags: { matrix: 'server-url' } });
      return 'juicyhouse.com';
    }
  }

  getClient() {
    return this.matrixClient;
  }

  getServerUrl() {
    return this.serverUrl;
  }

  getIsInitialized() {
    return this.isInitialized;
  }

  reset(options = {}) {
    const { preserveRecoveryAttempts = false } = options;
    if (this._recoveryStableTimer) {
      clearTimeout(this._recoveryStableTimer);
      this._recoveryStableTimer = null;
    }
    this._clientLifecycleOp = Promise.resolve();
    this._tokenExpiryHandling = null;
    this.initGeneration += 1;
    this.initPromise = null;

    this.teardownUnreadTotalTracking();
    this.clearRegisteredClientListeners();
    this._detachBrowserOnlineListener();
    // Tab-resume subscription stays attached for the process lifetime (singleton).

    if (this.matrixClient) {
      this.matrixClient.stopClient();
      this.matrixClient.removeAllListeners();
    }
    this.matrixClient = null;
    this.isInitialized = false;
    this.refreshPromise = null;
    this.syncRestartPromise = null;
    this.lastSyncRestartAt = 0;
    if (!preserveRecoveryAttempts) {
      this.recoveryAttempts = 0;
    }

    this._pendingRestart = null;
    this._pendingTabResume = null;
    this.clearInboxListSnapshot({ persist: false });
    clearMatrixSyncClock();

    this.emitTimelineReady(false);
    this.emitUnreadTotal(0);
  }

  async reinitialize(matrixUserInfo = null, options = {}) {
    this.reset({ preserveRecoveryAttempts: !!options.preserveRecoveryAttempts });
    return await this.initialize(matrixUserInfo);
  }
}

const matrix = new Matrix();

export default matrix;
